import React, { useState, useMemo } from 'react';
import { Trash2, Package, ArrowRightLeft } from 'lucide-react';
import { ProductionRecord } from '../../../types';
import { productionApi } from '../../../services/api';
import { usePermission } from '../../../hooks/usePermission';
import { Permissions } from '../../../constants/permissions';
import { TransferProductionDialog } from './TransferProductionDialog';

interface TodayProductionListProps {
  records: ProductionRecord[];
  onDelete: () => void;
}

export const TodayProductionList: React.FC<TodayProductionListProps> = ({ records, onDelete }) => {
  const canDelete = usePermission(Permissions.Production?.Delete);
  const canCreate = usePermission(Permissions.Production?.Create);
  
  const [transferDialog, setTransferDialog] = useState<{
    isOpen: boolean;
    productId: string;
    productName: string;
    unit: string;
    sourceDate: string;
    availableQuantity: number;
  } | null>(null);

  // Calculate available quantities by product and unit
  const availableQuantities = useMemo(() => {
    const quantities = new Map<string, number>();
    records.forEach(record => {
      const key = `${record.productId}-${record.unit}`;
      const current = quantities.get(key) || 0;
      quantities.set(key, current + record.quantity);
    });
    return quantities;
  }, [records]);

  const handleDelete = async (record: ProductionRecord) => {
    if (!confirm(`¿Estás seguro de que quieres eliminar este registro de producción de "${record.productName}"?`)) {
      return;
    }

    try {
      await productionApi.deleteRecord(record.id);
      onDelete();
    } catch (err: any) {
      alert('Error al eliminar el registro: ' + (err.message || err));
    }
  };

  const handleTransfer = (record: ProductionRecord) => {
    const key = `${record.productId}-${record.unit}`;
    const available = availableQuantities.get(key) || 0;
    
    setTransferDialog({
      isOpen: true,
      productId: record.productId,
      productName: record.productName,
      unit: record.unit,
      sourceDate: new Date(record.producedAt).toISOString().split('T')[0],
      availableQuantity: available,
    });
  };

  const handleTransferSuccess = () => {
    setTransferDialog(null);
    onDelete(); // Refresh the list
  };

  if (records.length === 0) {
    return (
      <div className="text-center py-8">
        <Package className="mx-auto mb-3 text-slate-300" size={48} />
        <p className="text-slate-500">No hay producción registrada para hoy</p>
        <p className="text-slate-400 text-sm mt-1">Usa el botón "Añadir Producción" para registrar</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        {/* Desktop View (Table) */}
        <table className="hidden xl:table w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Producto</th>
              <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Cantidad</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Unidad</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Hora</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Registrado por</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Observaciones</th>
              {(canDelete || canCreate) && (
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Acciones</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {records.map((record) => (
              <tr key={record.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <span className="font-medium text-slate-900">{record.productName}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-lg font-bold text-green-700">{record.quantity}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-slate-600">{record.unit}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-slate-600">
                    {new Date(record.producedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-slate-600">{record.registeredBy?.name || 'Unknown'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-slate-600">{record.observations || '-'}</span>
                </td>
                {(canDelete || canCreate) && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canCreate && record.quantity > 0 && (
                        <button
                          onClick={() => handleTransfer(record)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Mover unidades"
                        >
                          <ArrowRightLeft size={18} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(record)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile View (Cards) */}
        <div className="xl:hidden space-y-4">
          {records.map((record) => (
            <div key={record.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-slate-900 text-lg">{record.productName}</h4>
                <span className="text-sm text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded">
                  {new Date(record.producedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-black text-green-700">{record.quantity}</span>
                <span className="text-lg font-medium text-green-600">{record.unit}</span>
              </div>

              <div className="space-y-1 text-sm text-slate-600 mb-3 border-t border-slate-100 pt-3">
                <div className="flex justify-between">
                  <span>Registrado por:</span>
                  <span className="font-medium text-slate-900">{record.registeredBy?.name || 'Unknown'}</span>
                </div>
                {record.observations && (
                  <div className="flex justify-between">
                    <span>Nota:</span>
                    <span className="italic">{record.observations}</span>
                  </div>
                )}
              </div>

              {(canDelete || canCreate) && (
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  {canCreate && record.quantity > 0 && (
                    <button
                      onClick={() => handleTransfer(record)}
                      className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium flex-1 justify-center"
                    >
                      <ArrowRightLeft size={16} />
                      Mover
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(record)}
                      className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium flex-1 justify-center"
                    >
                      <Trash2 size={16} />
                      Eliminar
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Transfer Dialog */}
      {transferDialog && (
        <TransferProductionDialog
          isOpen={transferDialog.isOpen}
          onClose={() => setTransferDialog(null)}
          productId={transferDialog.productId}
          productName={transferDialog.productName}
          unit={transferDialog.unit}
          sourceDate={transferDialog.sourceDate}
          availableQuantity={transferDialog.availableQuantity}
          onSuccess={handleTransferSuccess}
        />
      )}
    </>
  );
};
